import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import JobWorkspace from "@/components/job-workspace";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { locationJobPath, locationJobReschedulesPath } from "@/routes";
import { Choice, Errors } from "../requests/controls";
import type { Job } from "../jobs/types";
import { Interval } from "./details";
import type { Schedule } from "./types";

type Values = Partial<
  Record<
    | "bay_id"
    | "starts_at"
    | "ends_at"
    | "starts_at_offset"
    | "ends_at_offset"
    | "reason"
    | "communication",
    string
  >
>;
export default function RescheduleForm({
  job,
  bays,
  time_zone,
  time_choices,
  input,
}: {
  job: {
    id: string;
    revision: string;
    representation: Job["representation"] & {
      locale: string;
      schedule: Schedule;
    };
  };
  bays: { id: string; name: string }[];
  time_zone: string;
  time_choices: Partial<Record<"starts_at" | "ends_at", string[]>>;
  input: Values;
}) {
  const { auth, i18n } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  return (
    <JobWorkspace
      primaryAction={false}
      breadcrumbs={[
        {
          title: t("jobs.confirmed"),
          href: locationJobPath(auth.location.id, job.id),
        },
      ]}
    >
      <Head title={t("reschedules.title")} />
      <PageCanvas padded={false}>
        <h2 className="text-page font-display">{t("reschedules.title")}</h2>
        <section className="space-y-2 rounded-lg border p-4">
          <h2 className="font-medium">{t("reschedules.original")}</h2>
          <Interval
            schedule={job.representation.schedule}
            locale={i18n.locale}
          />
        </section>
        <p className="text-muted-foreground text-sm">
          {t("reschedules.keep_original")}
        </p>
        <Form
          action={locationJobReschedulesPath(auth.location.id, job.id)}
          method="post"
        >
          {({ processing }) => (
            <div className="space-y-5">
              <Errors />
              <input type="hidden" name="revision" value={job.revision} />
              <fieldset className="space-y-4 rounded-lg border p-4">
                <legend className="px-2 font-medium">
                  {t("reschedules.replacement")}
                </legend>
                <p className="text-muted-foreground text-sm">
                  {t("jobs.zone_help", { zone: time_zone })}
                </p>
                <Choice
                  id="reschedule-bay"
                  name="reschedule[bay_id]"
                  label={t("jobs.bay_id")}
                  options={bays}
                  defaultValue={input.bay_id}
                />
                {(["starts_at", "ends_at"] as const).map((field) => (
                  <div key={field} className="space-y-2">
                    <Label htmlFor={`reschedule-${field}`}>
                      {t(`jobs.${field}`)}
                    </Label>
                    <Input
                      id={`reschedule-${field}`}
                      type="datetime-local"
                      name={`reschedule[${field}]`}
                      defaultValue={input[field]}
                      required
                    />
                    {(time_choices[field]?.length || 0) > 1 && (
                      <Choice
                        id={`reschedule-${field}-offset`}
                        name={`reschedule[${field}_offset]`}
                        label={t("jobs.occurrence")}
                        defaultValue={input[`${field}_offset`]}
                        options={time_choices[field]!.map((offset, index) => ({
                          id: offset,
                          name: t(
                            index === 0
                              ? "jobs.first_occurrence"
                              : "jobs.second_occurrence",
                            { offset },
                          ),
                        }))}
                      />
                    )}
                  </div>
                ))}
              </fieldset>
              {(["reason", "communication"] as const).map((field) => (
                <div key={field} className="space-y-2">
                  <Label htmlFor={`reschedule-${field}`}>
                    {t(`reschedules.${field}`)}
                  </Label>
                  <Textarea
                    id={`reschedule-${field}`}
                    name={`reschedule[${field}]`}
                    required
                    maxLength={500}
                    defaultValue={input[field]}
                  />
                  {field === "communication" && (
                    <p className="text-muted-foreground text-sm">
                      {t("reschedules.communication_help")}
                    </p>
                  )}
                </div>
              ))}
              <Button type="submit" disabled={processing}>
                {t("reschedules.propose")}
              </Button>
            </div>
          )}
        </Form>
      </PageCanvas>
    </JobWorkspace>
  );
}
